"use client";

import { useEffect, useMemo, useState, useRef } from "react";
import { AlertTriangle, ChevronDown, Info, Search, X } from "lucide-react";
import { Badge } from "./badge";
import { BottomSheet } from "./bottom-sheet";
import { Button } from "./button";
import { SideOverlayDrawer } from "./side-overlay-drawer";
import { Slider } from "./slider";
import { cn } from "../../lib/utils";
import { PRESENTATION_MODES, SETTINGS_TABS } from "../../constants";

// Filter option types matching artha-index API structure
export interface FilterOption {
  label: string;
  value: string;
  count?: number;
  isUnavailable?: boolean; // Indicates option is user-saved but not in API
}

export interface SalaryRangeInfo {
  min: number;
  max: number;
  currency: string;
  jobsWithSalary: number;
}

export interface FilterOptions {
  niches: FilterOption[];
  countries: FilterOption[];
  workModes: FilterOption[];
  jobTypes: FilterOption[];
  educationLevels: FilterOption[];
  experienceLevels?: FilterOption[]; // Add experience levels
  currencies: FilterOption[];
  salaryRange?: SalaryRangeInfo;
  // Employer tab options (from API)
  companies?: FilterOption[];
  industries?: FilterOption[];
}

export interface SettingsState {
  activeTab: "jobs" | "employer";
  niches: string[];
  positiveKeywords: string[];
  negativeKeywords: string[];
  countries: string[];
  minSalary: number;
  currencies: string[]; // Change to array
  workModes: string[];
  jobTypes: string[];
  educationLevels: string[]; // Change to array
  experienceLevels: string[]; // Change to array
  // Employer tab state
  companies: string[];
  companyRating: number;
  industries: string[];
}

export interface SettingsBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  initialData?: Partial<SettingsState>;
  onSave?: (state: SettingsState) => void;
  onFiltersChange?: (state: SettingsState) => void;
  availableOptions?: FilterOptions;
  estimatedJobCount?: number;
  isLoadingJobCount?: boolean;
  labels: SettingsBottomSheetLabels;
  // New props for self-contained settings
  profile?: any; // CreatorProfileForMyLink type
  updateSettingsMutation?: any; // Mutation hook
  presentation?: "bottom-sheet" | "drawer" | "inline";
  drawerClassName?: string;
  sheetClassName?: string;
  sheetOverlayClassName?: string;
  hideHeader?: boolean;
}

export interface SettingsBottomSheetLabels {
  headerTitle: string;
  headerSubtitle: string;
  tabJobs: string;
  tabEmployer: string;
  nicheTitle: string;
  nicheHelper: string;
  nichePlaceholder: string;
  nicheSearchPlaceholder: string;
  nicheEmptyMessage: string;
  keywordsTitle: string;
  keywordsHelper: string; // e.g., "Add relevant skills and technologies. Use commas to separate multiple keywords."
  keywordPlaceholder: string; // e.g., "Add skills (e.g., nodejs, java, react)"
  showLess: string;
  showMore: string;
  negativeKeywordsTitle: string;
  negativeKeywordsHelper: string; // e.g., "Exclude jobs with these keywords. Use commas to separate multiple keywords."
  negativeKeywordPlaceholder: string; // e.g., "Add keywords to exclude (e.g., senior, manager)"
  countryTitle: string;
  countryHelper: string;
  countryPlaceholder: string;
  countrySearchPlaceholder: string;
  countryEmptyMessage: string;
  minSalaryTitle: string;
  minSalaryHelper: string;
  currencyTitle: string;
  currencyHelper: string;
  currencyPlaceholder: string;
  currencyEmptyMessage: string;
  workModeTitle: string;
  workModeHelper: string;
  jobTypeTitle: string;
  jobTypeHelper: string;
  educationTitle: string;
  educationHelper: string;
  educationPlaceholder: string;
  educationEmptyMessage: string;
  experienceTitle: string;
  experienceHelper: string;
  experiencePlaceholder: string;
  experienceEmptyMessage: string;
  companyTitle: string;
  companyHelper: string;
  companyPlaceholder: string;
  companySearchPlaceholder: string;
  companyEmptyMessage: string;
  availableJobsLabel: string;
  availableJobsTooltip: string;
  loadingLabel: string;
  saveLabel: string;
  selectedCountLabel: string;
  noResultsMessage: string;
  noJobsLabel: string;
}

/**
 * Formats salary for display (e.g., 6017000 -> "$6M", 150000 -> "$150K")
 */
function formatSalary(amount: number): string {
  if (amount >= 1000000) {
    const millions = amount / 1000000;
    return `$${millions % 1 === 0 ? millions.toFixed(0) : millions.toFixed(1)}M`;
  }
  if (amount >= 1000) {
    const thousands = amount / 1000;
    return `$${thousands % 1 === 0 ? thousands.toFixed(0) : thousands.toFixed(0)}K`;
  }
  return `$${amount.toLocaleString()}`;
}

/**
 * Merges API options with user's saved selections.
 * User selections not in API get marked as unavailable.
 */
function mergeOptionsWithSaved(
  apiOptions: FilterOption[] | undefined,
  savedValues: string[],
  getLabelFn?: (value: string) => string
): FilterOption[] {
  const result: FilterOption[] = [];
  const apiValueSet = new Set((apiOptions || []).map((o) => o.value));

  // Add all API options first
  if (apiOptions) {
    result.push(...apiOptions);
  }

  // Add user's saved values that aren't in API (marked as unavailable)
  for (const value of savedValues) {
    if (!apiValueSet.has(value)) {
      result.push({
        label: getLabelFn ? getLabelFn(value) : value,
        value,
        count: 0,
        isUnavailable: true,
      });
    }
  }

  return result;
}

function formatCountLabel(template: string, count: number): string {
  return template.replace("{count}", String(count));
}

// Reusable Multi-Select Dropdown Component with Search
interface MultiSelectDropdownProps {
  options: FilterOption[];
  selected: string[];
  onToggle: (value: string) => void;
  placeholder: string;
  searchable?: boolean;
  searchPlaceholder: string;
  emptyMessage: string;
  selectedCountLabel: string;
  noResultsMessage: string;
  noJobsLabel: string;
}

function MultiSelectDropdown({
  options,
  selected,
  onToggle,
  placeholder,
  searchable = false,
  searchPlaceholder,
  emptyMessage,
  selectedCountLabel,
  noResultsMessage,
  noJobsLabel,
}: MultiSelectDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchQuery("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchable && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen, searchable]);

  const filteredOptions =
    searchable && searchQuery
      ? options.filter((opt) =>
        opt.label.toLowerCase().includes(searchQuery.toLowerCase())
      )
      : options;

  const displayText =
    selected.length === 0
      ? placeholder
      : formatCountLabel(selectedCountLabel, selected.length);

  // Check if any selected items are unavailable
  const hasUnavailableSelected = selected.some(
    (v) => options.find((o) => o.value === v)?.isUnavailable
  );

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "w-full flex items-center justify-between rounded-lg border bg-white px-3 py-2.5 text-sm text-[#101828]",
          hasUnavailableSelected ? "border-amber-400" : "border-black"
        )}
      >
        <span
          className={cn(
            selected.length === 0 ? "text-[#667085]" : "",
            hasUnavailableSelected && "flex items-center gap-1.5"
          )}
        >
          {hasUnavailableSelected && (
            <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
          )}
          {displayText}
        </span>
        <ChevronDown
          className={cn("h-4 w-4 transition-transform", isOpen && "rotate-180")}
        />
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-full rounded-lg border border-[#E4E7EC] bg-white shadow-lg overflow-hidden">
          {searchable && (
            <div className="p-2 border-b border-[#E4E7EC]">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#667085]" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={searchPlaceholder}
                  className="w-full pl-8 pr-3 py-2 text-sm border border-[#E4E7EC] rounded-md focus:outline-none focus:ring-1 focus:ring-[#0D41E1]"
                />
              </div>
            </div>
          )}
          <div className="max-h-48 overflow-y-auto">
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-4 text-sm text-[#667085] text-center">
                {searchQuery ? noResultsMessage : emptyMessage}
              </div>
            ) : (
              filteredOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => onToggle(option.value)}
                  className={cn(
                    "w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-[#F9FAFB]",
                    selected.includes(option.value) &&
                    !option.isUnavailable &&
                    "bg-[#EBF5FF]",
                    selected.includes(option.value) &&
                    option.isUnavailable &&
                    "bg-amber-50",
                    option.isUnavailable && "opacity-75"
                  )}
                >
                  <span className="flex items-center gap-2">
                    {option.isUnavailable && (
                      <AlertTriangle className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" />
                    )}
                    <span
                      className={option.isUnavailable ? "text-amber-700" : ""}
                    >
                      {option.label}
                    </span>
                  </span>
                  <span className="text-xs text-[#667085]">
                    {option.isUnavailable
                      ? noJobsLabel
                      : option.count?.toLocaleString()}
                  </span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Selected Chips Component with job count display
interface SelectedChipsProps {
  options: FilterOption[];
  selected: string[];
  onRemove: (value: string) => void;
  showCount?: boolean;
}

function SelectedChips({
  options,
  selected,
  onRemove,
  showCount = true,
}: SelectedChipsProps) {
  if (selected.length === 0) return null;

  const getOption = (value: string) => options.find((o) => o.value === value);

  return (
    <div className="flex flex-wrap gap-2 mt-2">
      {selected.map((value) => {
        const option = getOption(value);
        const isUnavailable = option?.isUnavailable;
        const count = option?.count;
        const displayLabel =
          showCount && count !== undefined
            ? `${option?.label ?? value} (${count.toLocaleString()})`
            : (option?.label ?? value);
        return (
          <Badge
            key={value}
            label={displayLabel}
            variant={isUnavailable ? "default" : "blue"}
            removable
            className={cn(
              "cursor-pointer",
              isUnavailable && "bg-amber-50 text-amber-700 border-amber-300"
            )}
            onClick={() => onRemove(value)}
          >
            <X className="ml-1 h-3 w-3" />
          </Badge>
        );
      })}
    </div>
  );
}

export function SettingsBottomSheet({
  isOpen,
  onClose,
  initialData,
  onSave,
  onFiltersChange,
  availableOptions,
  estimatedJobCount,
  isLoadingJobCount,
  labels,
  presentation = PRESENTATION_MODES.BOTTOM_SHEET,
  drawerClassName,
  sheetClassName,
  sheetOverlayClassName,
  hideHeader = false,
}: SettingsBottomSheetProps) {
  const [isAvailableJobsTooltipOpen, setIsAvailableJobsTooltipOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<"jobs" | "employer">(
    initialData?.activeTab ?? SETTINGS_TABS.JOBS
  );
  const [niches, setNiches] = useState<string[]>(initialData?.niches ?? []);
  const [positiveKeywords, setPositiveKeywords] = useState<string[]>(
    initialData?.positiveKeywords ?? []
  );
  const [negativeKeywords, setNegativeKeywords] = useState<string[]>(
    initialData?.negativeKeywords ?? []
  );
  const [countries, setCountries] = useState<string[]>(
    initialData?.countries ?? []
  );
  const [minSalary, setMinSalary] = useState<number[]>([
    initialData?.minSalary ?? 0,
  ]);
  const [currencies, setCurrencies] = useState<string[]>(
    initialData?.currencies ?? ["usd"]
  );
  const [workModes, setWorkModes] = useState<string[]>(
    initialData?.workModes ?? []
  );
  const [jobTypes, setJobTypes] = useState<string[]>(
    initialData?.jobTypes ?? []
  );
  const [educationLevels, setEducationLevels] = useState<string[]>(
    initialData?.educationLevels ?? []
  );
  const [experienceLevels, setExperienceLevels] = useState<string[]>(
    initialData?.experienceLevels ?? []
  );

  // Employer tab state
  const [companies, setCompanies] = useState<string[]>(
    initialData?.companies ?? []
  );
  const [companyRating, setCompanyRating] = useState<number[]>([
    initialData?.companyRating ?? 0,
  ]);
  const [industries, setIndustries] = useState<string[]>(
    initialData?.industries ?? []
  );

  const [keywordInput, setKeywordInput] = useState("");
  const [negKeywordInput, setNegKeywordInput] = useState("");
  const [showAllKeywords, setShowAllKeywords] = useState(false);

  const VISIBLE_KEYWORDS_COUNT = 8;

  // Reset state when sheet opens with new initial data
  useEffect(() => {
    if (isOpen && initialData) {
      if (initialData.activeTab) setActiveTab(initialData.activeTab);
      if (initialData.niches) setNiches(initialData.niches);
      if (initialData.positiveKeywords)
        setPositiveKeywords(initialData.positiveKeywords);
      if (initialData.negativeKeywords)
        setNegativeKeywords(initialData.negativeKeywords);
      if (initialData.countries) setCountries(initialData.countries);
      if (initialData.minSalary !== undefined)
        setMinSalary([initialData.minSalary]);
      if (initialData.currencies) setCurrencies(initialData.currencies);
      if (initialData.workModes) setWorkModes(initialData.workModes);
      if (initialData.jobTypes) setJobTypes(initialData.jobTypes);
      if (initialData.educationLevels)
        setEducationLevels(initialData.educationLevels);
      if (initialData.experienceLevels)
        setExperienceLevels(initialData.experienceLevels);
      // Employer tab
      if (initialData.companies) setCompanies(initialData.companies);
      if (initialData.companyRating !== undefined)
        setCompanyRating([initialData.companyRating]);
      if (initialData.industries) setIndustries(initialData.industries);
    }
  }, [initialData, isOpen]);

  // Merged options: API options + user's saved selections marked as unavailable
  const mergedNiches = useMemo(
    () =>
      mergeOptionsWithSaved(availableOptions?.niches, niches, (v) =>
        v
          .split("-")
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(" ")
      ),
    [availableOptions?.niches, niches]
  );

  const mergedCountries = useMemo(
    () => mergeOptionsWithSaved(availableOptions?.countries, countries),
    [availableOptions?.countries, countries]
  );

  const mergedWorkModes = useMemo(
    () => mergeOptionsWithSaved(availableOptions?.workModes, workModes),
    [availableOptions?.workModes, workModes]
  );

  const mergedJobTypes = useMemo(
    () => mergeOptionsWithSaved(availableOptions?.jobTypes, jobTypes),
    [availableOptions?.jobTypes, jobTypes]
  );

  const mergedEducationLevels = useMemo(
    () =>
      mergeOptionsWithSaved(availableOptions?.educationLevels, educationLevels),
    [availableOptions?.educationLevels, educationLevels]
  );

  const mergedExperienceLevels = useMemo(
    () =>
      mergeOptionsWithSaved(availableOptions?.experienceLevels, experienceLevels),
    [availableOptions?.experienceLevels, experienceLevels]
  );

  const mergedCurrencies = useMemo(
    () =>
      mergeOptionsWithSaved(availableOptions?.currencies, currencies),
    [availableOptions?.currencies, currencies]
  );

  // Helper function to check if a section should be shown
  // Always show sections if they have options, regardless of job count
  const shouldShowSection = (options: FilterOption[]) => {
    return options.length > 0;
  };

  // Check which sections should be visible - always show if we have options
  const showWorkModes = shouldShowSection(mergedWorkModes);
  const showJobTypes = shouldShowSection(mergedJobTypes);
  const showEducationLevels = shouldShowSection(mergedEducationLevels);
  const showExperienceLevels = shouldShowSection(mergedExperienceLevels);
  const showCurrencies = shouldShowSection(mergedCurrencies);

  // Build current state for callbacks
  const currentState = useMemo<SettingsState>(
    () => ({
      activeTab,
      niches,
      positiveKeywords,
      negativeKeywords,
      countries,
      minSalary: minSalary[0],
      currencies,
      workModes,
      jobTypes,
      educationLevels,
      experienceLevels,
      // Employer tab
      companies,
      companyRating: companyRating[0],
      industries,
    }),
    [
      activeTab,
      niches,
      positiveKeywords,
      negativeKeywords,
      countries,
      minSalary,
      currencies,
      workModes,
      jobTypes,
      educationLevels,
      experienceLevels,
      companies,
      companyRating,
      industries,
    ]
  );

  // Notify parent of filter changes (debounced in parent for job count)
  useEffect(() => {
    onFiltersChange?.(currentState);
  }, [currentState, onFiltersChange]);

  const handleSave = () => {
    onSave?.(currentState);
    onClose();
  };

  const toggleItem = (
    list: string[],
    item: string,
    setter: (val: string[]) => void
  ) => {
    if (list.includes(item)) {
      setter(list.filter((i) => i !== item));
    } else {
      setter([...list, item]);
    }
  };

  const addKeyword = (
    input: string,
    list: string[],
    setter: (val: string[]) => void,
    clearInput: () => void
  ) => {
    if (input.trim()) {
      // Split by comma and process each keyword
      const keywords = input
        .split(',')
        .map(kw => kw.trim())
        .filter(kw => kw && !list.includes(kw)); // Filter out empty strings and duplicates

      if (keywords.length > 0) {
        setter([...list, ...keywords]);
        clearInput();
      }
    }
  };

  const visibleKeywords = showAllKeywords
    ? positiveKeywords
    : positiveKeywords.slice(0, VISIBLE_KEYWORDS_COUNT);
  const hiddenKeywordsCount = positiveKeywords.length - VISIBLE_KEYWORDS_COUNT;

  // Check if there are any unavailable selections
  const hasUnavailableSelections = useMemo(() => {
    return (
      niches.some(
        (v) => mergedNiches.find((o) => o.value === v)?.isUnavailable
      ) ||
      countries.some(
        (v) => mergedCountries.find((o) => o.value === v)?.isUnavailable
      ) ||
      workModes.some(
        (v) => mergedWorkModes.find((o) => o.value === v)?.isUnavailable
      ) ||
      jobTypes.some(
        (v) => mergedJobTypes.find((o) => o.value === v)?.isUnavailable
      ) ||
      educationLevels.some(
        (v) => mergedEducationLevels.find((o) => o.value === v)?.isUnavailable
      ) ||
      experienceLevels.some(
        (v) => mergedExperienceLevels.find((o) => o.value === v)?.isUnavailable
      ) ||
      currencies.some(
        (v) => mergedCurrencies.find((o) => o.value === v)?.isUnavailable
      )
    );
  }, [
    niches,
    countries,
    workModes,
    jobTypes,
    educationLevels,
    experienceLevels,
    currencies,
    mergedNiches,
    mergedCountries,
    mergedWorkModes,
    mergedJobTypes,
    mergedEducationLevels,
    mergedExperienceLevels,
    mergedCurrencies,
  ]);

  const content = (
    <div className="relative flex flex-1 flex-col">
      {/* Scrollable content area - hidden scrollbar */}
      <div className="flex-1 space-y-4 pr-1">
        {/* Header */}
        <header className="space-y-1">
          {!hideHeader && presentation !== PRESENTATION_MODES.DRAWER && (
            <p className="text-lg font-semibold text-[#344054]">
              {labels.headerTitle}
            </p>
          )}
          {!hideHeader ? (
            <p className="text-sm text-[#667085]">
              {labels.headerSubtitle}
            </p>
          ) : null}
        </header>

        {/* Tabs */}
        <div className="flex gap-2 border-b z-10 bg-[#FFFAEF] sm:bg-[#ffffff73] max-sm:sticky -top-2 pt-2 border-[#E4E7EC]">
          <button
            type="button"
            onClick={() => setActiveTab(SETTINGS_TABS.JOBS)}
            className={cn(
              "pb-2 px-3 text-sm font-medium transition-colors",
              activeTab === SETTINGS_TABS.JOBS
                ? "text-[#0D41E1] border-b-2 border-[#0D41E1]"
                : "text-[#667085]"
            )}
          >
            {labels.tabJobs}
          </button>
          <button
            type="button"
            onClick={() => setActiveTab(SETTINGS_TABS.EMPLOYER)}
            className={cn(
              "pb-2 px-3 text-sm font-medium transition-colors",
              activeTab === SETTINGS_TABS.EMPLOYER
                ? "text-[#0D41E1] border-b-2 border-[#0D41E1]"
                : "text-[#667085]"
            )}
          >
            {labels.tabEmployer}
          </button>
        </div>

        {/* Jobs Tab Content */}
        {activeTab === SETTINGS_TABS.JOBS && (
          <div className="space-y-5">
            {/* Niche - Multi-select dropdown with search */}
            {/* <FieldBlock
                title={labels.nicheTitle}
                helper={labels.nicheHelper}
              >
                <MultiSelectDropdown
                  options={mergedNiches}
                  selected={niches}
                  onToggle={(value) => toggleItem(niches, value, setNiches)}
                  placeholder={labels.nichePlaceholder}
                  searchable={true}
                  searchPlaceholder={labels.nicheSearchPlaceholder}
                  emptyMessage={labels.nicheEmptyMessage}
                  selectedCountLabel={labels.selectedCountLabel}
                  noResultsMessage={labels.noResultsMessage}
                  noJobsLabel={labels.noJobsLabel}
                />
                <SelectedChips
                  options={mergedNiches}
                  selected={niches}
                  onRemove={(value) => toggleItem(niches, value, setNiches)}
                />
              </FieldBlock>

              <Divider /> */}

            {/* Keywords */}
            <FieldBlock
              title={labels.keywordsTitle}
              helper={labels.keywordsHelper}
            >
              <div className="flex gap-2">
                <input
                  className="flex-1 rounded-lg border border-black bg-white px-3 py-2 text-sm text-[#101828] focus:outline-none"
                  placeholder={labels.keywordPlaceholder}
                  value={keywordInput}
                  onChange={(e) => setKeywordInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === "Tab") {
                      e.preventDefault();
                      addKeyword(
                        keywordInput,
                        positiveKeywords,
                        setPositiveKeywords,
                        () => setKeywordInput("")
                      );
                    }
                  }}
                  onBlur={() => {
                    if (keywordInput.trim()) {
                      addKeyword(
                        keywordInput,
                        positiveKeywords,
                        setPositiveKeywords,
                        () => setKeywordInput("")
                      );
                    }
                  }}
                />
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {visibleKeywords.map((kw) => (
                  <Badge
                    key={kw}
                    label={kw}
                    variant="blue"
                    className="cursor-pointer"
                    removable
                    onClick={() =>
                      toggleItem(positiveKeywords, kw, setPositiveKeywords)
                    }
                  >
                    <X className="ml-1 h-3 w-3" />
                  </Badge>
                ))}
                {hiddenKeywordsCount > 0 && (
                  <button
                    type="button"
                    onClick={() => setShowAllKeywords(!showAllKeywords)}
                    className="text-xs text-[#0D41E1] font-medium hover:underline"
                  >
                    {showAllKeywords
                      ? labels.showLess
                      : formatCountLabel(
                        labels.showMore,
                        hiddenKeywordsCount
                      )}
                  </button>
                )}
              </div>
            </FieldBlock>

            <Divider />

            {/* Negative Keywords */}
            <FieldBlock
              title={labels.negativeKeywordsTitle}
              helper={labels.negativeKeywordsHelper}
            >
              <div className="flex gap-2">
                <input
                  className="flex-1 rounded-lg border border-black bg-white px-3 py-2 text-sm text-[#101828] focus:outline-none"
                  placeholder={labels.negativeKeywordPlaceholder}
                  value={negKeywordInput}
                  onChange={(e) => setNegKeywordInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === "Tab") {
                      e.preventDefault();
                      addKeyword(
                        negKeywordInput,
                        negativeKeywords,
                        setNegativeKeywords,
                        () => setNegKeywordInput("")
                      );
                    }
                  }}
                  onBlur={() => {
                    if (negKeywordInput.trim()) {
                      addKeyword(
                        negKeywordInput,
                        negativeKeywords,
                        setNegativeKeywords,
                        () => setNegKeywordInput("")
                      );
                    }
                  }}
                />
              </div>
              {negativeKeywords.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {negativeKeywords.map((kw) => (
                    <Badge
                      key={kw}
                      label={kw}
                      variant="default"
                      removable
                      className="bg-red-50 text-red-600 border-red-200 cursor-pointer"
                      onClick={() =>
                        toggleItem(negativeKeywords, kw, setNegativeKeywords)
                      }
                    >
                      <X className="ml-1 h-3 w-3" />
                    </Badge>
                  ))}
                </div>
              )}
            </FieldBlock>

            <Divider />

            {/* Country - Multi-select dropdown */}
            {mergedCountries.length > 0 && (
              <FieldBlock
                title={labels.countryTitle}
                helper={labels.countryHelper}
              >
                <MultiSelectDropdown
                  options={mergedCountries}
                  selected={countries}
                  onToggle={(value) =>
                    toggleItem(countries, value, setCountries)
                  }
                  placeholder={labels.countryPlaceholder}
                  searchable={true}
                  searchPlaceholder={labels.countrySearchPlaceholder}
                  emptyMessage={labels.countryEmptyMessage}
                  selectedCountLabel={labels.selectedCountLabel}
                  noResultsMessage={labels.noResultsMessage}
                  noJobsLabel={labels.noJobsLabel}
                />
                <SelectedChips
                  options={mergedCountries}
                  selected={countries}
                  onRemove={(value) =>
                    toggleItem(countries, value, setCountries)
                  }
                />
              </FieldBlock>
            )}

            <Divider />

            {/* Minimum Salary */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-[#667085]">
                    {labels.minSalaryTitle}
                  </p>
                  <p className="text-xs text-[#667085]">
                    {labels.minSalaryHelper}
                  </p>
                </div>
                <span className="text-base font-bold text-[#ED5AA9]">
                  ${minSalary[0].toLocaleString()}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs text-[#667085] min-w-[30px]">
                  {formatSalary(availableOptions?.salaryRange?.min ?? 0)}
                </span>
                <Slider
                  min={availableOptions?.salaryRange?.min ?? 0}
                  max={availableOptions?.salaryRange?.max ?? 200000}
                  step={5000}
                  value={minSalary}
                  onValueChange={setMinSalary}
                  showRange={false}
                  className="flex-1"
                />
                <span className="text-xs text-[#667085] min-w-[40px]">
                  {formatSalary(availableOptions?.salaryRange?.max ?? 200000)}
                </span>
              </div>
            </div>

            <Divider />

            {/* Currency */}
            <FieldBlock
              title={labels.currencyTitle}
              helper={labels.currencyHelper}
            >
              <MultiSelectDropdown
                options={mergedCurrencies}
                selected={currencies}
                onToggle={(value) => toggleItem(currencies, value, setCurrencies)}
                placeholder={labels.currencyPlaceholder}
                searchable={false}
                searchPlaceholder={labels.currencyPlaceholder}
                emptyMessage={labels.currencyEmptyMessage}
                selectedCountLabel={labels.selectedCountLabel}
                noResultsMessage={labels.noResultsMessage}
                noJobsLabel={labels.noJobsLabel}
              />
              <SelectedChips
                options={mergedCurrencies}
                selected={currencies}
                onRemove={(value) => toggleItem(currencies, value, setCurrencies)}
              />
            </FieldBlock>

            <Divider />

            {/* Work Mode - Only show if there are options with jobs */}
            {showWorkModes && (
              <>
                <FieldBlock
                  title={labels.workModeTitle}
                  helper={labels.workModeHelper}
                >
                  <div className="flex flex-wrap gap-2">
                    {mergedWorkModes.map((mode) => {
                      const isSelected = workModes.includes(mode.value);
                      return (
                        <Badge
                          key={mode.value}
                          label={mode.label}
                          variant={
                            isSelected && !mode.isUnavailable ? "blue" : "default"
                          }
                          className={cn(
                            "cursor-pointer",
                            mode.isUnavailable &&
                            isSelected &&
                            "bg-amber-50 text-amber-700 border-amber-300",
                            mode.isUnavailable && !isSelected && "opacity-50"
                          )}
                          onClick={() =>
                            toggleItem(workModes, mode.value, setWorkModes)
                          }
                        >
                          {isSelected && mode.isUnavailable && (
                            <AlertTriangle className="mr-1 h-3 w-3 text-amber-500" />
                          )}
                          {isSelected && <X className="ml-1 h-3 w-3" />}
                        </Badge>
                      );
                    })}
                  </div>
                </FieldBlock>

                <Divider />
              </>
            )}

            {/* Job Type - Only show if there are options with jobs */}
            {showJobTypes && (
              <>
                <FieldBlock
                  title={labels.jobTypeTitle}
                  helper={labels.jobTypeHelper}
                >
                  <div className="flex flex-wrap gap-2">
                    {mergedJobTypes.map((type) => {
                      const isSelected = jobTypes.includes(type.value);
                      return (
                        <Badge
                          key={type.value}
                          label={type.label}
                          variant={
                            isSelected && !type.isUnavailable ? "blue" : "default"
                          }
                          className={cn(
                            "cursor-pointer",
                            type.isUnavailable &&
                            isSelected &&
                            "bg-amber-50 text-amber-700 border-amber-300",
                            type.isUnavailable && !isSelected && "opacity-50"
                          )}
                          onClick={() =>
                            toggleItem(jobTypes, type.value, setJobTypes)
                          }
                        >
                          {isSelected && type.isUnavailable && (
                            <AlertTriangle className="mr-1 h-3 w-3 text-amber-500" />
                          )}
                          {isSelected && <X className="ml-1 h-3 w-3" />}
                        </Badge>
                      );
                    })}
                  </div>
                </FieldBlock>

                <Divider />
              </>
            )}

            {/* Education Level - Only show if there are options with jobs */}
            {showEducationLevels && (
              <>
                <FieldBlock
                  title={labels.educationTitle}
                  helper={labels.educationHelper}
                >
                  <MultiSelectDropdown
                    options={mergedEducationLevels}
                    selected={educationLevels}
                    onToggle={(value) => toggleItem(educationLevels, value, setEducationLevels)}
                    placeholder={labels.educationPlaceholder}
                    searchable={false}
                    searchPlaceholder={labels.educationPlaceholder}
                    emptyMessage={labels.educationEmptyMessage}
                    selectedCountLabel={labels.selectedCountLabel}
                    noResultsMessage={labels.noResultsMessage}
                    noJobsLabel={labels.noJobsLabel}
                  />
                  <SelectedChips
                    options={mergedEducationLevels}
                    selected={educationLevels}
                    onRemove={(value) => toggleItem(educationLevels, value, setEducationLevels)}
                  />
                </FieldBlock>

                <Divider />
              </>
            )}

            {/* Experience Level - Only show if there are options */}
            {showExperienceLevels && (
              <>
                <FieldBlock
                  title={labels.experienceTitle}
                  helper={labels.experienceHelper}
                >
                  <MultiSelectDropdown
                    options={mergedExperienceLevels}
                    selected={experienceLevels}
                    onToggle={(value) => toggleItem(experienceLevels, value, setExperienceLevels)}
                    placeholder={labels.experiencePlaceholder}
                    searchable={false}
                    searchPlaceholder={labels.experiencePlaceholder}
                    emptyMessage={labels.experienceEmptyMessage}
                    selectedCountLabel={labels.selectedCountLabel}
                    noResultsMessage={labels.noResultsMessage}
                    noJobsLabel={labels.noJobsLabel}
                  />
                  <SelectedChips
                    options={mergedExperienceLevels}
                    selected={experienceLevels}
                    onRemove={(value) => toggleItem(experienceLevels, value, setExperienceLevels)}
                  />
                </FieldBlock>
              </>
            )}
          </div>
        )}

        {/* Employer Tab Content */}
        {activeTab === SETTINGS_TABS.EMPLOYER && (
          <div className="space-y-5">
            {/* Company Filter */}
            <FieldBlock
              title={labels.companyTitle}
              helper={labels.companyHelper}
            >
              <MultiSelectDropdown
                options={availableOptions?.companies || []}
                selected={companies}
                onToggle={(value) =>
                  toggleItem(companies, value, setCompanies)
                }
                placeholder={labels.companyPlaceholder}
                searchable={true}
                searchPlaceholder={labels.companySearchPlaceholder}
                emptyMessage={labels.companyEmptyMessage}
                selectedCountLabel={labels.selectedCountLabel}
                noResultsMessage={labels.noResultsMessage}
                noJobsLabel={labels.noJobsLabel}
              />
              <SelectedChips
                options={availableOptions?.companies || []}
                selected={companies}
                onRemove={(value) =>
                  toggleItem(companies, value, setCompanies)
                }
              />
            </FieldBlock>

            {/* <Divider /> */}

            {/* Company Rating */}
            {/* <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-[#667085]">
                      Company Rating
                    </p>
                    <p className="text-xs text-[#667085]">
                      Set a minimum company rating for quality control.
                    </p>
                  </div>
                  <span className="text-base font-bold text-[#ED5AA9] flex items-center gap-1">
                    {companyRating[0].toFixed(1)}
                    <span className="text-[#ED5AA9]">★</span>
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-[#667085] flex items-center gap-0.5">
                    0 <span className="text-[#667085]">★</span>
                  </span>
                  <Slider
                    min={0}
                    max={5}
                    step={0.5}
                    value={companyRating}
                    onValueChange={setCompanyRating}
                    showRange={false}
                    className="flex-1"
                  />
                  <span className="text-xs text-[#667085] flex items-center gap-0.5">
                    5 <span className="text-[#667085]">★</span>
                  </span>
                </div>
              </div> */}

            {/* <Divider /> */}

            {/* Industry Filter */}
            {/* <FieldBlock
                title="Industry"
                helper="Filter jobs based on the company's industry focus."
              >
                <MultiSelectDropdown
                  options={availableOptions?.industries || []}
                  selected={industries}
                  onToggle={(value) =>
                    toggleItem(industries, value, setIndustries)
                  }
                  placeholder="Select Industry"
                  searchable={true}
                  searchPlaceholder="Search industries..."
                  emptyMessage="No industries available"
                />
                <SelectedChips
                  options={availableOptions?.industries || []}
                  selected={industries}
                  onRemove={(value) =>
                    toggleItem(industries, value, setIndustries)
                  }
                />
              </FieldBlock> */}
          </div>
        )}
      </div>

      {/* Sticky Footer with estimated jobs and save */}
      <div className="sticky -bottom-6 sm:bottom-0 z-10 mt-4 bg-[#FFFAEF] pt-3 pb-2 -mx-3 px-3 border-t border-[#E4E7EC]">
        <div className="flex items-center justify-between">
          <div className="flex flex-col">
            <div className="relative flex items-center gap-2 text-sm font-semibold text-[#667085]">
              <span>{labels.availableJobsLabel}</span>
              <button
                type="button"
                aria-label={`${labels.availableJobsLabel} info`}
                onClick={() =>
                  setIsAvailableJobsTooltipOpen((previous) => !previous)
                }
                onBlur={() => {
                  window.setTimeout(() => {
                    setIsAvailableJobsTooltipOpen(false);
                  }, 100);
                }}
                className="group relative inline-flex items-center"
              >
                <Info className="h-4 w-4 text-[#667085]" />
                <span
                  className={cn(
                    "absolute bottom-full left-0 z-10 mb-2 hidden w-56 rounded-md bg-[#101828] p-2 text-left text-[10px] font-normal leading-4 text-white shadow-lg group-hover:block",
                    isAvailableJobsTooltipOpen && "block"
                  )}
                >
                  {labels.availableJobsTooltip}
                  <span className="absolute left-2 top-full -ml-1 border-4 border-transparent border-t-[#101828]" />
                </span>
              </button>
            </div>
            <p className="text-base font-bold text-[#ED5AA9]">
              {isLoadingJobCount ? (
                <span className="text-[#667085] font-normal">
                  {labels.loadingLabel}
                </span>
              ) : estimatedJobCount !== undefined ? (
                estimatedJobCount.toLocaleString()
              ) : (
                <span className="text-[#667085] font-normal">—</span>
              )}
            </p>
          </div>
          <Button size="lg" className="rounded-lg px-8" onClick={handleSave}>
            {labels.saveLabel}
          </Button>
        </div>
      </div>
    </div>
  );

  if (presentation === PRESENTATION_MODES.DRAWER) {
    return (
      <SideOverlayDrawer
        isOpen={isOpen}
        onClose={onClose}
        title={labels.headerTitle}
        onBack={onClose}
        className={cn("px-5 pb-6 pt-5", drawerClassName)}
      >
        {content}
      </SideOverlayDrawer>
    );
  }

  if (presentation === PRESENTATION_MODES.INLINE) {
    return (
      <div className={cn("bg-transparent text-[#101828]", sheetClassName)}>
        {content}
      </div>
    );
  }

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      className={cn("bg-[#FFFAEF] px-3 pt-2 text-[#101828]", sheetClassName)}
      overlayClassName={sheetOverlayClassName}
      showDragHandle
      title={null}
    >
      {content}
    </BottomSheet>
  );
}

function Divider() {
  return <div className="h-px w-full bg-[#D4D4D4]" />;
}

function FieldBlock({
  title,
  helper,
  children,
}: {
  title: string;
  helper?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <p className="text-sm font-medium text-[#667085]">{title}</p>
        {helper && <p className="text-xs text-[#667085]">{helper}</p>}
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}
